import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid,
	Snackbar,
	TextField,
} from "@material-ui/core";
import { Alert, Autocomplete, Color } from "@material-ui/lab";
import { get } from "lodash-es";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { getHeat } from "../../../repositories/HeatRepository";
import { useModalStyles } from "../../../styles/modalStyles";
import { useGradeContext } from "../../context/GradesContext";
import { useLadleContext } from "../../context/LadlesContext";
import { INITIAL_VALUES } from "../../models/Heats/Initial-values";
import { deleteHeat } from "../../repositories/heatsRepository";

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	heatIdForDelete: number | null;
};

export const DeleteHeatModal = ({ show, onHide, heatIdForDelete }: Props) => {
	const classes = useModalStyles();

	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState<Color | undefined>(undefined);

	const [error, setError] = useState<string | null>(null);

	const [heatForDelete, setHeatForDelete] = useState<any | null>(null);

	const {
		state: { ladles },
	} = useLadleContext();
	const {
		state: { grades },
	} = useGradeContext();

	const { control, setValue } = useForm<any>({
		defaultValues: INITIAL_VALUES,
	});

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};

	const handleDelete = async () => {
		setIsSubmitLoading(true);

		if (heatIdForDelete) {
			const res = await deleteHeat(heatIdForDelete);
			if (res.ok) {
				setMessage("The heat was deleted successfully");
				setMessageType("success");
				onHide(true);
				setHeatForDelete(null);
			} else {
				setMessage("Error, please try again");
				setMessageType("error");
				setError(res.message);
			}

			setIsSubmitLoading(false);
			setOpenAlertMessage(true);
		}
	};
	useEffect(() => {
		if (show) {
			(async () => {
				setIsLoading(true);
				if (heatIdForDelete) {
					const resp = await getHeat(heatIdForDelete);
					if (resp.ok) {
						const heat = get(resp, "data.tables[0].rows[0]", null);
						if (heat) {
							setHeatForDelete(heat);
						} else {
							onHide(true);
							setMessage("Error, heat not found. Try again");
							setOpenAlertMessage(true);
							setHeatForDelete(null);
						}
					} else {
						setError(resp.message);
					}
				}

				setIsLoading(false);
			})();
		}
	}, [show]);

	useEffect(() => {
		if (show && heatForDelete) {
			const {
				HeatId,
				Name,
				GradeId,
				LadleId,
				IsExternal,
				SteelWeight,
				ProductionYear,
			} = heatForDelete;

			setValue("HeatId", HeatId);
			setValue("HeatName", Name);
			setValue("GradeId", GradeId);
			setValue("SteelWeight", SteelWeight);
			setValue("LadleId", LadleId);
			setValue("IsExternal", IsExternal);
			setValue("ProductionYear", ProductionYear);
		}
	}, [heatForDelete]);

	return (
		<>
			<MesfModal
				open={show}
				handleClose={() => {
					onHide(true);
				}}
				id="deleteHeat"
				title={"Delete a heat"}
				maxWidth="sm"
			>
				<MesfModal.Content
					className={classes.modalBody}
					style={{ padding: "15px 30px" }}
					dividers
				>
					{isLoading && (
						<Grid
							container
							justifyContent="center"
							alignItems="center"
							style={{ minHeight: "150px" }}
						>
							<CircularProgress size="5rem" />
						</Grid>
					)}

					{!isLoading && heatForDelete && (
						<>
							<Grid container spacing={1}>
								<Grid
									item
									xs={12}
									container
									spacing={2}
									justifyContent="center"
									alignItems="center"
								>
									<Grid item md={8} xs={12}>
										<Controller
											name="HeatName"
											control={control}
											render={({ field }) => {
												return (
													<TextField
														disabled
														label="Name"
														value={field.value}
														variant="outlined"
														fullWidth
														margin="dense"
													/>
												);
											}}
										/>
									</Grid>
									<Grid item xs={4}>
										<Controller
											name="ProductionYear"
											control={control}
											render={({ field }) => {
												return (
													<TextField
														disabled
														label="Year"
														value={field.value}
														variant="outlined"
														fullWidth
														margin="dense"
													/>
												);
											}}
										/>
									</Grid>
								</Grid>

								<Grid
									item
									xs={12}
									container
									spacing={2}
									justifyContent="center"
									alignItems="center"
								>
									<Grid item xs={4}>
										<Controller
											name="GradeId"
											control={control}
											render={({ field }) => (
												<Autocomplete
													disabled
													id="clear-on-escape"
													clearOnEscape
													options={grades}
													getOptionLabel={(option) => option.GradeName}
													renderInput={(params) => (
														<TextField
															{...params}
															label="Grade"
															variant="outlined"
															fullWidth
															size="small"
														/>
													)}
													value={
														grades.find(
															(grade) => grade.GradeId === field.value,
														) || null
													}
												/>
											)}
										/>
									</Grid>

									<Grid item xs={4} style={{ paddingTop: "4px" }}>
										<Controller
											name="SteelWeight"
											control={control}
											render={({ field }) => (
												<TextField
													disabled
													type={"number"}
													label="Steel Weight"
													value={field.value}
													variant="outlined"
													fullWidth
													margin="dense"
													size="small"
												/>
											)}
										/>
									</Grid>
									<Grid item xs={4}>
										<Controller
											name="LadleId"
											control={control}
											render={({ field }) => (
												<Autocomplete
													disabled
													options={ladles}
													getOptionLabel={(option) => option.LadleName}
													renderInput={(params) => (
														<TextField
															{...params}
															label="Ladle"
															variant="outlined"
															fullWidth
															size="small"
														/>
													)}
													value={
														ladles.find(
															(ladle) => ladle.LadleId === field.value,
														) || null
													}
												/>
											)}
										/>
									</Grid>
								</Grid>
								<Grid
									item
									xs={12}
									container
									spacing={2}
									justifyContent="center"
									alignItems="center"
								>
									<Grid item xs={12}>
										<Controller
											name="IsExternal"
											control={control}
											render={({ field }) => (
												<FormControlLabel
													className={classes.formControlLabel}
													control={
														(
															<Checkbox
																disabled
																checked={field.value === true}
																color="primary"
																style={{ width: "100% !important" }}
															/>
														) as any
													}
													label="External"
												/>
											)}
										/>
									</Grid>
								</Grid>
							</Grid>
						</>
					)}
				</MesfModal.Content>
				{!isLoading && (
					<MesfModal.Actions>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									size="small"
									variant="contained"
									color="default"
									onClick={() => {
										onHide(false);
										setHeatForDelete(null);
									}}
								>
									Cancel
								</Button>
							</Grid>
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									size="small"
									startIcon={
										isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="secondary"
									onClick={handleDelete}
								>
									Delete
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				)}
			</MesfModal>
			<Snackbar
				open={openAlertMessage}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity={messageType} onClose={handleClose}>
					{message}
				</Alert>
			</Snackbar>
			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
